import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EventIcon from "@mui/icons-material/Event";
import { Link as RouterLink } from "react-router-dom";
import { AppContext } from "../Store";
import { useSnackbar } from "../shared/SnackbarProvider";
import { StatStrip } from "../shared/StatStrip";
import {
  addFundingPayment,
  createReceipt,
  deleteFundingPayment,
  formatPounds,
  issueInvoice,
  reissueInvoice,
  termFromCalendars,
  termIdFor,
  today,
  updateInvoice,
  watchChildren,
  watchFundingPayments,
  watchCalendars,
  watchInvoices,
  type FundingPayment,
  type PreschoolCalendar,
  type PreschoolChild,
  type PreschoolInvoice,
  type PreschoolInvoiceInput,
} from "../shared/preschoolInvoices";
import { InvoiceStatus, STATUS_LABEL, byNewest, groupByChild, matchesSearch, notInvoiced, statusOf, totals } from "./preschoolInvoices/list";
import { InvoiceRow, RowActions, RowHeader } from "./preschoolInvoices/InvoiceRow";
import { FundingPanel, NotInvoicedPanel } from "./preschoolInvoices/Panels";
import { EditorTarget, InvoiceEditor } from "./preschoolInvoices/InvoiceEditor";
import { PdfDialog } from "./preschoolInvoices/PdfDialog";

type StatusFilter = "all" | "notIssued" | "unpaid" | "paid" | "cancelled";
const SEASONS = ["spring", "summer", "autumn"];
const termOrder = (id: string) => parseInt(id.slice(0, 4), 10) * 3 + SEASONS.indexOf(id.slice(5));
const money = (pence: number) => `£${formatPounds(pence)}`;

const PreschoolInvoicesPage: React.FC = () => {
  const { userContext } = useContext(AppContext);
  const snack = useSnackbar();

  const [invoices, setInvoices] = useState<PreschoolInvoice[] | null>(null);
  const [children, setChildren] = useState<PreschoolChild[]>([]);
  const [calendars, setCalendars] = useState<PreschoolCalendar[]>([]);
  const [payments, setPayments] = useState<FundingPayment[]>([]);
  const [termFilter, setTermFilter] = useState<string>("all");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [grouped, setGrouped] = useState(false);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [receiptFor, setReceiptFor] = useState<PreschoolInvoice | null>(null);
  const [pdfFor, setPdfFor] = useState<PreschoolInvoice | null>(null);
  const [paymentDate, setPaymentDate] = useState(today());
  const [undo, setUndo] = useState<{ message: string; revert: () => Promise<void> } | null>(null);

  const onError = (error: Error) => snack(`Could not load preschool invoices: ${error.message}`, "error");
  useEffect(() => watchInvoices(setInvoices, onError), []);
  useEffect(() => watchChildren(setChildren, onError), []);
  useEffect(() => watchCalendars(setCalendars, onError), []);
  useEffect(() => watchFundingPayments(setPayments, onError), []);

  const currentTermId = termIdFor(today());
  const termFor = (id: string) => termFromCalendars(calendars, id).term;
  const openFor = (id: string) => termFromCalendars(calendars, id).open;
  const termIds = useMemo(() => {
    const ids = new Set([currentTermId, ...(invoices ?? []).map((i) => i.termId).filter((id): id is string => !!id && /^\d{4}-(spring|summer|autumn)$/.test(id))]);
    return [...ids].sort((a, b) => termOrder(b) - termOrder(a));
  }, [invoices, currentTermId]);

  const inTerm = useMemo(
    () => (invoices ?? []).filter((i) => termFilter === "all" || i.termId === termFilter),
    [invoices, termFilter]
  );
  const shown = useMemo(() => inTerm
    .filter((i) => {
      const s = statusOf(i);
      return status === "all" || s === status || (status === "paid" && s === "nothingDue");
    })
    .filter((i) => matchesSearch(i, search))
    .sort(byNewest), [inTerm, status, search]);
  const sums = totals(inTerm, payments.filter((p) => termFilter === "all" || p.termId === termFilter));
  const counts: Record<StatusFilter, number> = {
    all: inTerm.length,
    notIssued: inTerm.filter((i) => statusOf(i) === "notIssued").length,
    unpaid: inTerm.filter((i) => statusOf(i) === "unpaid").length,
    paid: inTerm.filter((i) => ["paid", "nothingDue"].includes(statusOf(i))).length,
    cancelled: inTerm.filter((i) => statusOf(i) === "cancelled").length,
  };
  const missing = useMemo(() => notInvoiced(children, invoices ?? [], currentTermId), [children, invoices, currentTermId]);
  const lastRef = invoices?.length ? [...invoices].sort(byNewest)[0].ref : null;

  if (!import.meta.env.DEV && !userContext?.isAdmin()) {
    return <Box sx={{ padding: 2 }}><Alert severity="warning">Only the manager can see preschool invoicing.</Alert></Box>;
  }

  const run = async (label: string, work: () => Promise<void>) => {
    setBusy(label);
    try {
      await work();
    } catch (error) {
      snack(`${label} failed: ${error instanceof Error ? error.message : String(error)}`, "error");
    } finally {
      setBusy(null);
    }
  };

  const quick = async (message: string, apply: () => Promise<void>, revert: () => Promise<void>) => {
    try {
      await apply();
      setUndo({ message, revert });
    } catch (error) {
      snack(`Could not save: ${error instanceof Error ? error.message : String(error)}`, "error");
    }
  };

  const previousFor = (childId: string) =>
    (invoices ?? []).filter((i) => i.childId === childId && i.kind === "term" && i.calc && !i.cancelled).sort(byNewest)[0] ?? null;
  const editorTermId = termFilter === "all" ? currentTermId : termFilter;
  const editorTerm = termFor(editorTermId);

  const actions: RowActions = {
    newForChild: (invoice) => setEditor({
      mode: "create", child: children.find((c) => c.id === invoice.childId) ?? null, previous: previousFor(invoice.childId),
      term: editorTerm, open: openFor(editorTermId),
    }),
    edit: (invoice) => setEditor({
      mode: "edit", invoice, term: termFor(invoice.termId ?? currentTermId), open: openFor(invoice.termId ?? currentTermId),
    }),
    setPaid: (invoice, paid) => {
      const before = { paid: invoice.paid, paidDate: invoice.paidDate };
      quick(`${invoice.ref} ${invoice.childName} marked ${paid ? "paid" : "unpaid"}`,
        () => updateInvoice(invoice.ref, { paid, paidDate: paid ? today() : null }),
        () => updateInvoice(invoice.ref, before));
    },
    setCancelled: (invoice, cancelled) => quick(`${invoice.ref} ${cancelled ? "cancelled" : "restored"}`,
      () => updateInvoice(invoice.ref, { cancelled }), () => updateInvoice(invoice.ref, { cancelled: !cancelled })),
    receipt: (invoice) => {
      setPaymentDate(invoice.paidDate || today());
      setReceiptFor(invoice);
    },
    preview: (invoice) => setPdfFor(invoice),
  };

  const submitEditor = async (input: PreschoolInvoiceInput) => {
    if (editor?.mode === "edit") {
      const result = await reissueInvoice(editor.invoice.ref, input);
      snack(`${result.ref} ${editor.invoice.notIssued ? "issued" : "reissued"} (${money(result.totalPence)}); draft in Gmail`, "success");
    } else {
      const result = await issueInvoice(input);
      snack(`${result.ref} issued (${money(result.totalPence)})` + (result.error ? `, but the draft failed: ${result.error}` : "; draft in Gmail"),
        result.error ? "warning" : "success");
    }
    setEditor(null);
  };

  const createReceiptNow = () => run("Creating receipt", async () => {
    const invoice = receiptFor!;
    setReceiptFor(null);
    await createReceipt(invoice.ref, paymentDate);
    snack(`Receipt for ${invoice.ref} drafted in Gmail; marked paid`, "success");
  });

  const fundingTerms = (termFilter === "all" ? termIds : [termFilter]).map(termFor);

  return (
    <Box sx={{ px: { xs: 1.5, md: 3 }, py: 2, maxWidth: 1480, mx: "auto" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={600}>Preschool invoices</Typography>
          <Typography variant="body2" color="text.secondary">
            {invoices ? `${invoices.length} invoices${lastRef ? ` · latest ${lastRef}` : ""} · newest first` : "Loading…"}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<EventIcon />} component={RouterLink} to="/preschool-calendar">Calendar</Button>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => setEditor({ mode: "create", child: null, previous: null, term: editorTerm, open: openFor(editorTermId) })}>
          New invoice
        </Button>
      </Stack>

      {invoices === null && <CircularProgress />}
      {invoices && (
        <>
          <StatStrip items={[
            { label: "Invoiced to parents", color: "primary", value: money(sums.parentPence), hint: `${inTerm.filter((i) => !i.cancelled && !i.notIssued).length} invoices` },
            { label: "Paid by parents", color: "success", value: money(sums.paidPence),
              hint: sums.parentPence ? `${Math.round((sums.paidPence / sums.parentPence) * 100)}% collected` : undefined,
              progress: sums.parentPence ? (sums.paidPence / sums.parentPence) * 100 : 0 },
            { label: "Outstanding", color: "info", value: money(sums.outstandingPence), hint: `${sums.unpaidCount} unpaid` },
            { label: "Council funding", color: "secondary", value: money(sums.fundingReceivedPence),
              hint: `of ${money(sums.fundingExpectedPence)} expected`,
              progress: sums.fundingExpectedPence ? (sums.fundingReceivedPence / sums.fundingExpectedPence) * 100 : 0 },
            ...(counts.notIssued ? [{ label: "Not issued", color: "warning" as const, value: String(counts.notIssued), hint: "numbered, not sent yet",
              onClick: () => setStatus("notIssued") }] : []),
          ]} />

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 2 }} useFlexGap flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Term</InputLabel>
              <Select label="Term" value={termFilter} onChange={(e) => setTermFilter(e.target.value)}>
                <MenuItem value="all">All terms</MenuItem>
                {termIds.map((id) => <MenuItem key={id} value={id}>{termFor(id).name}</MenuItem>)}
              </Select>
            </FormControl>
            <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, value) => value && setStatus(value)}>
              {(["all", "notIssued", "unpaid", "paid", "cancelled"] as StatusFilter[]).filter((s) => s !== "notIssued" || counts.notIssued > 0).map((s) => (
                <ToggleButton key={s} value={s}>{s === "all" ? "All" : STATUS_LABEL[s as InvoiceStatus]} {counts[s]}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            <FormControlLabel control={<Switch checked={grouped} onChange={(e) => setGrouped(e.target.checked)} />} label="Group by child" />
            <Box sx={{ flexGrow: 1 }} />
            <TextField size="small" placeholder="Search BHP number, child, parent" value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 260 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          </Stack>

          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 1fr) 320px" }, alignItems: "start" }}>
            <Paper variant="outlined" sx={{ overflow: "hidden", order: { xs: 2, xl: 1 } }}>
              <RowHeader />
              {!grouped && shown.map((invoice) => <InvoiceRow key={invoice.ref} invoice={invoice} busy={!!busy} actions={actions} />)}
              {grouped && groupByChild(shown).map((group) => (
                <Box key={group.childId}>
                  <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ px: 2, py: 1, bgcolor: "action.selected" }}>
                    <Typography fontWeight={700}>{group.childName}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }} noWrap>
                      {group.parentName} · {group.invoices.length} invoice{group.invoices.length === 1 ? "" : "s"}
                    </Typography>
                    <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {money(group.parentPence)}{group.outstandingPence ? ` · ${money(group.outstandingPence)} outstanding` : ""}
                    </Typography>
                  </Stack>
                  {group.invoices.map((invoice) => <InvoiceRow key={invoice.ref} invoice={invoice} busy={!!busy} actions={actions} hideChild />)}
                </Box>
              ))}
              {shown.length === 0 && (
                <Box sx={{ p: 4, textAlign: "center" }}><Typography color="text.secondary">No invoices match.</Typography></Box>
              )}
            </Paper>

            <Box sx={{ order: { xs: 1, xl: 2 }, display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "1fr" }, alignItems: "start" }}>
              <NotInvoicedPanel term={termFor(currentTermId)} items={missing}
                onCreate={(item) => setEditor({
                  mode: "create", child: item.child, previous: item.previous, term: termFor(currentTermId), open: openFor(currentTermId),
                })} />
              <FundingPanel terms={fundingTerms} invoices={invoices} payments={payments}
                onAdd={(payment) => run("Saving funding payment", () => addFundingPayment(payment))}
                onDelete={(payment) => quick(`Funding payment of ${money(payment.amountPence)} deleted`,
                  () => deleteFundingPayment(payment.id), async () => { const { id, ...rest } = payment; await addFundingPayment(rest); })} />
            </Box>
          </Box>
        </>
      )}

      <InvoiceEditor target={editor} children={children} invoices={invoices ?? []} onClose={() => setEditor(null)} onSubmit={submitEditor} />
      <PdfDialog invoice={pdfFor} onClose={() => setPdfFor(null)} />

      <Dialog open={!!receiptFor} onClose={() => setReceiptFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>Receipt for {receiptFor?.ref}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Marks the invoice paid and leaves a receipt draft for {receiptFor?.parentName}
            {receiptFor ? ` (${money(receiptFor.totalPence)})` : ""}.
          </DialogContentText>
          <TextField label="Payment date" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
            InputLabelProps={{ shrink: true }} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiptFor(null)}>Cancel</Button>
          <Button variant="contained" disabled={!paymentDate} onClick={createReceiptNow}>Create receipt</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!undo}
        autoHideDuration={6000}
        onClose={(_, reason) => reason !== "clickaway" && setUndo(null)}
        message={undo?.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        action={
          <Button color="inherit" size="small" sx={{ fontWeight: 700 }} onClick={() => {
            const revert = undo!.revert;
            setUndo(null);
            revert().catch((error) => snack(`Could not undo: ${error.message}`, "error"));
          }}>
            Undo
          </Button>
        }
      />
    </Box>
  );
};

export default PreschoolInvoicesPage;
